import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  BellIcon,
  CheckIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  EnvelopeIcon,
  MegaphoneIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  UserGroupIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import { baseUrl } from '../../baseurl.js';
axios.defaults.baseURL = baseUrl;

const Notification = () => {
  const [notifications, setNotifications] = useState([]);
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  
  // Modal states
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [currentNotification, setCurrentNotification] = useState(null);
  const [getUsers, setGetUsers] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'info',
    audience: 'all',
    user_list: [],
    status: 'active',
    scheduled: false,
    schedule_date: '',
    schedule_time: ''
  });

  // Load notifications and users on component mount
  useEffect(() => {
    loadNotifications();
    getAllUsers();
  }, []);

  // Filter notifications when search or filters change
  useEffect(() => {
    filterNotifications();
  }, [searchTerm, filterStatus, filterType, notifications]);

  // API: Load all notifications
  const loadNotifications = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("vsat_token");
      const response = await axios.get('admin/notification', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        setNotifications(response.data.notifications || []);
        setFilteredNotifications(response.data.notifications || []);
      } else {
        toast.error("Failed to fetch notifications");
        setNotifications([]);
        setFilteredNotifications([]);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Error loading notifications");
      setNotifications([]);
      setFilteredNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  // API: Get all users for user selection
  const getAllUsers = async () => {
    try {
      const token = localStorage.getItem("vsat_token");
      const response = await axios.get('admin/fetch-users', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        setGetUsers(response.data.users || []);
      } else {
        setGetUsers([]);
        toast.error("Failed to fetch users");
      }
    } catch (error) {
      setGetUsers([]);
      toast.error(error.response?.data?.message || "Error fetching users");
    }
  };

  const filterNotifications = () => {
    let filtered = notifications;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(notification =>
        notification.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        notification.message?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(notification => notification.status === filterStatus);
    }

    // Type filter
    if (filterType !== 'all') {
      filtered = filtered.filter(notification => notification.type === filterType);
    }

    setFilteredNotifications(filtered);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterStatusChange = (e) => {
    setFilterStatus(e.target.value);
  };

  const handleFilterTypeChange = (e) => {
    setFilterType(e.target.value);
  };

  // Selection handlers
  const toggleNotificationSelection = (notificationId) => {
    setSelectedNotifications(prev =>
      prev.includes(notificationId)
        ? prev.filter(id => id !== notificationId)
        : [...prev, notificationId]
    );
  };

  const toggleSelectAll = () => {
    setSelectedNotifications(prev =>
      prev.length === filteredNotifications.length && filteredNotifications.length > 0
        ? []
        : filteredNotifications.map(notification => notification._id)
    );
  };

  // Modal handlers
  const openAddModal = () => {
    setFormData({
      title: '',
      message: '',
      type: 'info',
      audience: 'all',
      user_list: [],
      status: 'active',
      scheduled: false,
      schedule_date: '',
      schedule_time: ''
    });
    setIsAddModalOpen(true);
  };

  const openEditModal = (notification) => {
    setCurrentNotification(notification);
    setFormData({
      title: notification.title || '',
      message: notification.message || '',
      type: notification.type || 'info',
      audience: notification.audience || 'all',
      user_list: notification.user_list || [],
      status: notification.status || 'active',
      scheduled: notification.scheduled || false,
      schedule_date: notification.schedule_date || '',
      schedule_time: notification.schedule_time || ''
    });
    setIsEditModalOpen(true);
  };

  const openDeleteModal = (notification = null) => {
    setCurrentNotification(notification);
    setIsDeleteModalOpen(true);
  };

  const closeModals = () => {
    setIsAddModalOpen(false);
    setIsEditModalOpen(false);
    setIsDeleteModalOpen(false);
    setCurrentNotification(null);
  };

  // Form handlers
  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Reset user_list when audience is changed to 'all'
    if (field === 'audience' && value === 'all') {
      setFormData(prev => ({
        ...prev,
        user_list: []
      }));
    }
  };

  const handleUserSelection = (userId) => {
    setFormData(prev => {
      const userList = prev.user_list.includes(userId)
        ? prev.user_list.filter(id => id !== userId)
        : [...prev.user_list, userId];
      
      return {
        ...prev,
        user_list: userList
      };
    });
  };

  const selectAllUsers = () => {
    if (formData.user_list.length === getUsers.length) {
      // Deselect all
      setFormData(prev => ({
        ...prev,
        user_list: []
      }));
    } else {
      // Select all
      setFormData(prev => ({
        ...prev,
        user_list: getUsers.map(user => user._id)
      }));
    }
  };

  // API: Save notification
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem("vsat_token");
      let response;

      if (isAddModalOpen) {
        // Add new notification
        response = await axios.post('admin/save-notification', formData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      } else if (isEditModalOpen) {
        // Update notification
        response = await axios.put(`admin/edit-notification/${currentNotification._id}`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      }

      if (response.data.success) {
        toast.success(isAddModalOpen ? 'Notification created successfully' : 'Notification updated successfully');
        loadNotifications(); // Reload the list
        closeModals();
      } else {
        toast.error(response.data.message || 'Failed to save notification');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error saving notification');
    } finally {
      setLoading(false);
    }
  };

  // API: Delete notification
  const handleDelete = async () => {
    try {
      const token = localStorage.getItem("vsat_token");
      let response;

      if (currentNotification) {
        // Single delete
        response = await axios.delete(`admin/delete-notification/${currentNotification._id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      } else if (selectedNotifications.length > 0) {
        // Multiple delete - you might need to implement a bulk delete endpoint
        response = await axios.post('admin/delete-notifications', {
          notification_ids: selectedNotifications
        }, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      }

      if (response?.data.success) {
        toast.success(currentNotification ? 'Notification deleted successfully' : `${selectedNotifications.length} notifications deleted successfully`);
        loadNotifications(); // Reload the list
        setSelectedNotifications([]);
      } else {
        toast.error(response?.data.message || 'Failed to delete notification');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error deleting notification');
    } finally {
      setIsDeleteModalOpen(false);
      setCurrentNotification(null);
    }
  };

  const deleteAll = async () => {
    if (window.confirm('Are you sure you want to delete all notifications? This action cannot be undone.')) {
      try {
        const token = localStorage.getItem("vsat_token");
        const response = await axios.delete('admin/delete-all-notifications', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data.success) {
          toast.success('All notifications deleted successfully');
          setNotifications([]);
          setSelectedNotifications([]);
        } else {
          toast.error('Failed to delete all notifications');
        }
      } catch (error) {
        toast.error(error.response?.data?.message || 'Error deleting all notifications');
      }
    }
  };

  // API: Toggle notification status
  const toggleNotificationStatus = async (notificationId) => {
    try {
      const token = localStorage.getItem("vsat_token");
      const response = await axios.put(`admin/toggle-notification-status/${notificationId}`, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        toast.success('Notification status updated successfully');
        loadNotifications(); // Reload the list
      } else {
        toast.error('Failed to update notification status');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error updating notification status');
    }
  };

  // Helper functions
  const getTypeBadge = (type) => {
    const styles = {
      info: 'bg-blue-100 text-blue-800',
      warning: 'bg-yellow-100 text-yellow-800',
      critical: 'bg-red-100 text-red-800',
      success: 'bg-green-100 text-green-800'
    };
    const icons = {
      info: <InformationCircleIcon className="w-4 h-4" />,
      warning: <ExclamationTriangleIcon className="w-4 h-4" />,
      critical: <MegaphoneIcon className="w-4 h-4" />,
      success: <CheckIcon className="w-4 h-4" />
    };
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${styles[type] || 'bg-gray-100 text-gray-800'}`}>
        {icons[type]}
        <span className="ml-1 capitalize">{type}</span>
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
        {status}
      </span>
    );
  };

  const getAudienceBadge = (audience) => {
    const styles = {
      all: 'bg-purple-100 text-purple-800',
      specific: 'bg-orange-100 text-orange-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[audience] || 'bg-gray-100 text-gray-800'}`}>
        {audience}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };


  if (loading && notifications.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ToastContainer position="top-right" autoClose={3000} />
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Notification Management</h1>
          <p className="text-gray-600">Total notifications: {notifications.length}</p>
        </div>
        <div className="flex space-x-3">
          {selectedNotifications.length > 0 && (
            <button
              onClick={() => openDeleteModal()}
              className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <TrashIcon className="w-4 h-4 mr-2" />
              Delete Selected ({selectedNotifications.length})
            </button>
          )}
          <button
            onClick={deleteAll}
            className="flex items-center px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
          >
            <TrashIcon className="w-4 h-4 mr-2" />
            Delete All
          </button>
          <button
            onClick={openAddModal}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Add Notification
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search notifications by title or message..."
                value={searchTerm}
                onChange={handleSearch}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex items-center space-x-2">
              <FunnelIcon className="w-5 h-5 text-gray-400" />
              <select
                value={filterStatus}
                onChange={handleFilterStatusChange}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="flex items-center space-x-2">
              <FunnelIcon className="w-5 h-5 text-gray-400" />
              <select
                value={filterType}
                onChange={handleFilterTypeChange}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Types</option>
                <option value="info">Info</option>
                <option value="warning">Warning</option>
                <option value="critical">Critical</option>
                <option value="success">Success</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Notifications Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-12 px-6 py-3">
                  <input
                    type="checkbox"
                    checked={selectedNotifications.length === filteredNotifications.length && filteredNotifications.length > 0}
                    onChange={toggleSelectAll}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Notification
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Audience
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Schedule
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredNotifications.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                    {loading ? 'Loading notifications...' : 'No notifications found matching your criteria'}
                  </td>
                </tr>
              ) : (
                filteredNotifications.map((notification) => (
                  <tr key={notification._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedNotifications.includes(notification._id)}
                        onChange={() => toggleNotificationSelection(notification._id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {notification.title}
                        </div>
                        <div className="text-sm text-gray-500 line-clamp-2">
                          {notification.message}
                        </div>
                        {notification.audience === 'specific' && notification.user_list && notification.user_list.length > 0 && (
                          <div className="text-xs text-gray-400 mt-1">
                            {notification.user_list.length} user(s) selected
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getTypeBadge(notification.type)}
                    </td>
                    <td className="px-6 py-4">
                      {getAudienceBadge(notification.audience)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        {getStatusBadge(notification.status)}
                        <button
                          onClick={() => toggleNotificationStatus(notification._id)}
                          className={`p-1 rounded ${
                            notification.status === 'active' 
                              ? 'text-green-600 hover:bg-green-100' 
                              : 'text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          {notification.status === 'active' ? 
                            <EyeIcon className="w-4 h-4" /> : 
                            <EyeSlashIcon className="w-4 h-4" />
                          }
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {formatDate(notification.createdAt)}
                      
                    </td>
                    <td>
                          
                         {formatDate(notification.schedule_date) ?? 'NA'}
                      
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => openEditModal(notification)}
                          className="p-2 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                          title="Edit"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openDeleteModal(notification)}
                          className="p-2 text-red-600 hover:bg-red-100 rounded transition-colors"
                          title="Delete"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {(isAddModalOpen || isEditModalOpen) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <BellIcon className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    {isAddModalOpen ? 'Create New Notification' : 'Edit Notification'}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {isAddModalOpen ? 'Create a new notification for users' : 'Update the notification details'}
                  </p>
                </div>
              </div>
              <button
                onClick={closeModals}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={(e) => handleFormChange('title', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter notification title"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type *
                  </label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={(e) => handleFormChange('type', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="info">Information</option>
                    <option value="warning">Warning</option>
                    <option value="critical">Critical</option>
                    <option value="success">Success</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Audience *
                  </label>
                  <select
                    name="user"
                    value={formData.audience}
                    onChange={(e) => handleFormChange('audience', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Users</option>
                    <option value="specific">Specific Users</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status *
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={(e) => handleFormChange('status', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              {/* User Selection for Specific Audience */}
              {formData.audience === 'specific' && (
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Select Users *
                    </label>
                    <button
                      type="button"
                      onClick={selectAllUsers}
                      className="text-sm text-blue-600 hover:text-blue-700"
                    >
                      {formData.user_list.length === getUsers.length ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>
                  <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg">
                    {getUsers.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">No users available</div>
                    ) : (
                      getUsers.map((user) => (
                        <div key={user._id} className="flex items-center p-3 border-b border-gray-100 last:border-b-0">
                          <input
                            type="checkbox"
                            checked={formData.user_list.includes(user._id)}
                            onChange={() => handleUserSelection(user._id)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="ml-3 text-sm text-gray-700 capitalize">
                            {user.name} ({user.ip})
                          </span>
                          
                        </div>
                      ))
                    )}
                  </div>
                  <div className="mt-2 text-sm text-gray-500">
                    {formData.user_list.length} user(s) selected
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message *
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => handleFormChange('message', e.target.value)}
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter notification message"
                  required
                />
              </div>

              {/* Schedule Options */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center mb-4">
                  <input
                    type="checkbox"
                    checked={formData.scheduled}
                    onChange={(e) => handleFormChange('scheduled', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label className="ml-2 text-sm font-medium text-gray-700">
                    Schedule for later
                  </label>
                </div>
                
                {formData.scheduled && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Schedule Date
                      </label>
                      <input
                        type="date"
                        value={formData.schedule_date}
                        onChange={(e) => handleFormChange('schedule_date', e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Schedule Time
                      </label>
                      <input
                        type="time"
                        value={formData.schedule_time}
                        onChange={(e) => handleFormChange('schedule_time', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeModals}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {loading ? 'Saving...' : (isAddModalOpen ? 'Create Notification' : 'Update Notification')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-2 bg-red-100 rounded-lg">
                  <TrashIcon className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {currentNotification ? 'Delete Notification' : 'Delete Selected Notifications'}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {currentNotification 
                      ? `Are you sure you want to delete "${currentNotification.title}"?` 
                      : `Are you sure you want to delete ${selectedNotifications.length} notifications?`
                    }
                  </p>
                </div>
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={closeModals}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Notification;